// src/owner/pages/PaymentActivation.jsx
import { useState, useEffect } from "react";
import { API_BASE_URL } from "../../config/api";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (document.getElementById("razorpay-script")) return resolve(true);
    const script = document.createElement("script");
    script.id  = "razorpay-script";
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload  = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const PaymentActivation = () => {
  const [upi,           setUpi]           = useState(null);
  const [sub,           setSub]           = useState(null);
  const [method,        setMethod]        = useState("GPay");
  const [txnId,         setTxnId]         = useState("");
  const [submitting,    setSubmitting]    = useState(false);
  const [rzpLoading,    setRzpLoading]    = useState(false);
  const [msg,           setMsg]           = useState({ type: "", text: "" });
  const [step,          setStep]          = useState(1);

  const token = localStorage.getItem("token");

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE_URL}/api/settings/upi`).then(r => r.json()),
      fetch(`${API_BASE_URL}/api/subscription/my`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json()),
    ]).then(([upiData, subData]) => {
      setUpi(upiData);
      setSub(subData);
      if (subData?.subscription?.status === "pending") setStep(3);
    }).catch(() => {});
  }, []);

  // ── Razorpay online payment ───────────────────────────────
  const handleRazorpayPayment = async () => {
    setRzpLoading(true);
    setMsg({ type: "", text: "" });
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        setMsg({ type: "error", text: "Failed to load Razorpay. Check your internet connection." });
        setRzpLoading(false);
        return;
      }

      const res  = await fetch(`${API_BASE_URL}/api/razorpay/create-order`, {
        method:  "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (!res.ok) {
        setMsg({ type: "error", text: data.message || "Could not initiate payment." });
        setRzpLoading(false);
        return;
      }

      const options = {
        key:         data.keyId,
        amount:      data.amount,
        currency:    data.currency,
        name:        "FraudCheck FDS",
        description: "Standard Plan — 1 Year Subscription",
        order_id:    data.orderId,
        prefill: { name: data.name, email: data.email },
        theme: { color: "#4318FF" },
        handler: async (response) => {
          // Verify payment on backend
          const verifyRes = await fetch(`${API_BASE_URL}/api/razorpay/verify`, {
            method:  "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body:    JSON.stringify(response),
          });
          const verifyData = await verifyRes.json();
          if (verifyRes.ok) {
            setMsg({ type: "success", text: verifyData.message });
            setStep(4); // success step
          } else {
            setMsg({ type: "error", text: verifyData.message || "Payment verification failed." });
          }
        },
        modal: {
          ondismiss: () => setRzpLoading(false),
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        setMsg({ type: "error", text: `Payment failed: ${response.error.description}` });
        setRzpLoading(false);
      });
      rzp.open();
    } catch {
      setMsg({ type: "error", text: "Something went wrong. Please try again." });
    }
    setRzpLoading(false);
  };

  const handleSubmit = async () => {
    if (!txnId.trim())           { setMsg({ type: "error", text: "Please enter your Transaction ID." }); return; }
    if (txnId.trim().length < 6) { setMsg({ type: "error", text: "Transaction ID looks too short. Please check and re-enter." }); return; }

    setSubmitting(true); setMsg({ type: "", text: "" });
    try {
      const res = await fetch(`${API_BASE_URL}/api/subscription/upload-proof`, {
        method: "POST",
        headers: {
          Authorization:  `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          paymentMethod: method,
          transactionId: txnId.trim(),
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setStep(3);
      } else {
        setMsg({ type: "error", text: data.message || "Submission failed. Try again." });
      }
    } catch {
      setMsg({ type: "error", text: "Network error. Please try again." });
    }
    setSubmitting(false);
  };

  // ── Amount with GST ──────────────────────────────────────
  const base       = upi?.amount     || 300;
  const gstEnabled = upi?.gstEnabled || false;
  const gstPercent = upi?.gstPercent || 18;
  const gstAmount  = gstEnabled ? Math.round(base * gstPercent / 100) : 0;
  const amt        = base + gstAmount;   // total charged

  const upiLink = upi?.upiId
    ? `upi://pay?pa=${upi.upiId}&pn=${encodeURIComponent(upi.accountName || "FDS")}&am=${amt}&cu=INR&tn=FDS+Subscription`
    : "#";
  const qrUrl   = upi?.qrCodeUrl || (upi?.upiId
    ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(upiLink)}`
    : "");

  const isMobile = typeof navigator !== "undefined" && /Android|iPhone|iPad/i.test(navigator.userAgent);

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #f4f7fe 0%, #e8ecff 100%)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ width: "100%", maxWidth: 500 }}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 36, marginBottom: 6 }}>🚗</div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#1b2559", margin: 0 }}>FraudCheck FDS</h1>
          <p style={{ color: "#6b7280", fontSize: 13, marginTop: 4 }}>Activate your account to get started</p>
        </div>

        {/* Step indicator */}
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 4, marginBottom: 20 }}>
          {[`Pay ₹${amt}${gstEnabled ? " (incl. GST)" : ""}`, "Enter Transaction ID", "Activation Pending"].map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12, fontWeight: 700,
                  background: step > i + 1 ? "#16a34a" : step === i + 1 ? "#4318FF" : "#e0e5f2",
                  color: step >= i + 1 ? "#fff" : "#a3aed0",
                }}>
                  {step > i + 1 ? "✓" : i + 1}
                </div>
                <div style={{ fontSize: 10, color: step === i + 1 ? "#4318FF" : "#a3aed0", fontWeight: step === i + 1 ? 700 : 400, textAlign: "center", maxWidth: 80 }}>
                  {s}
                </div>
              </div>
              {i < 2 && <div style={{ width: 24, height: 2, background: step > i + 1 ? "#16a34a" : "#e0e5f2", margin: "0 4px", marginBottom: 18 }} />}
            </div>
          ))}
        </div>

        <div style={{ background: "#fff", borderRadius: 20, padding: 28, boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>

          {/* ── STEP 1: PAY ── */}
          {step === 1 && (
            <>
              <h2 style={{ fontSize: 17, fontWeight: 800, color: "#1b2559", margin: "0 0 6px" }}>Pay ₹{amt} to Activate</h2>
              <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 20, lineHeight: 1.6 }}>
                {upi?.note || `Pay ₹${amt} via UPI or Cash to activate your FDS Standard plan for 1 year.`}
              </p>

              {/* Amount with GST breakdown */}
              <div style={{ background: "#ede9fe", borderRadius: 12, padding: "16px 20px", textAlign: "center", marginBottom: 20, border: "1.5px solid #4318FF20" }}>
                <div style={{ fontSize: 36, fontWeight: 900, color: "#4318FF" }}>₹{amt}</div>
                <div style={{ fontSize: 12, color: "#7551FF", marginTop: 2 }}>One-time yearly subscription — Standard Plan</div>
                {gstEnabled && (
                  <div style={{ marginTop: 10, padding: "8px 12px", background: "rgba(255,255,255,0.6)", borderRadius: 8, fontSize: 12, color: "#4318FF" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span>Base price</span><span style={{ fontWeight: 700 }}>₹{base}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, color: "#6b7280" }}>
                      <span>GST ({gstPercent}%)</span><span style={{ fontWeight: 700 }}>₹{gstAmount}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #4318FF30", paddingTop: 6 }}>
                      <span style={{ fontWeight: 800 }}>Total payable</span><span style={{ fontWeight: 800 }}>₹{amt}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* QR Code */}
              {qrUrl && (
                <div style={{ textAlign: "center", marginBottom: 20 }}>
                  <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>Scan QR code with any UPI app</p>
                  <div style={{ display: "inline-block", padding: 12, border: "2px solid #4318FF20", borderRadius: 16, background: "#f9faff" }}>
                    <img src={qrUrl} alt="UPI QR" style={{ width: 180, height: 180, display: "block" }} />
                  </div>
                  <p style={{ fontSize: 13, fontWeight: 700, color: "#1b2559", margin: "8px 0 0" }}>{upi?.accountName}</p>
                  <p style={{ fontSize: 12, color: "#6b7280", margin: "2px 0 0" }}>{upi?.upiId}</p>
                </div>
              )}

              {/* Pay button — single UPI chooser button, works on all mobile apps */}
              {upi?.upiId && (
                <div style={{ marginBottom: 20 }}>
                  {isMobile ? (
                    // Mobile — single button opens UPI app chooser (GPay/PhonePe/Paytm)
                    <a href={upiLink}
                      style={{ display: "block", padding: 14, background: "#4318FF", color: "#fff",
                        borderRadius: 12, textDecoration: "none", fontWeight: 800, fontSize: 15,
                        textAlign: "center", boxShadow: "0 4px 16px rgba(67,24,255,0.3)" }}>
                      📱 Pay ₹{amt} with UPI App
                    </a>
                  ) : (
                    // Desktop — show info to scan QR
                    <div style={{ background: "#f4f7fe", borderRadius: 10, padding: "12px 14px", textAlign: "center" }}>
                      <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>
                        📱 Scan the QR code above with <b>GPay, PhonePe, Paytm</b> or any UPI app on your phone
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div style={{ background: "#fef3c7", borderRadius: 10, padding: "12px 14px", marginBottom: 20, border: "1.5px solid #fde68a" }}>
                <p style={{ fontSize: 12, color: "#92400e", margin: 0, lineHeight: 1.7 }}>
                  ⚠️ <strong>Note:</strong> After paying, copy your <strong>Transaction ID / UPI Reference Number</strong> from your payment app. You will enter it in the next step. For cash payments, use the receipt number given by admin.
                </p>
              </div>

              {/* ── Razorpay online payment button ── */}
              <button
                onClick={handleRazorpayPayment}
                disabled={rzpLoading}
                style={{ width: "100%", padding: 14, borderRadius: 12, border: "none", cursor: rzpLoading ? "not-allowed" : "pointer",
                  background: rzpLoading ? "#a3aed0" : "#16a34a", color: "#fff", fontWeight: 800, fontSize: 15, marginBottom: 12,
                  boxShadow: rzpLoading ? "none" : "0 4px 16px rgba(22,163,74,0.3)" }}>
                {rzpLoading ? "Opening Payment..." : "💳 Pay Online with Razorpay (Instant Activation)"}
              </button>

              <div style={{ textAlign: "center", fontSize: 12, color: "#a3aed0", marginBottom: 12 }}>— or pay via UPI / Cash and enter Transaction ID below —</div>

              <button onClick={() => setStep(2)}
                style={{ width: "100%", padding: 14, borderRadius: 12, border: "1.5px solid #4318FF", cursor: "pointer", background: "#fff", color: "#4318FF", fontWeight: 800, fontSize: 15 }}>
                I Have Already Paid — Enter Transaction ID →
              </button>

              {msg.text && (
                <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 8, fontWeight: 600, fontSize: 13,
                  background: msg.type === "error" ? "#fee2e2" : "#dcfce7",
                  color:      msg.type === "error" ? "#dc2626"  : "#16a34a" }}>
                  {msg.type === "error" ? "❌ " : "✅ "}{msg.text}
                </div>
              )}
            </>
          )}

          {/* ── STEP 4: RAZORPAY INSTANT SUCCESS ── */}
          {step === 4 && (
            <div style={{ textAlign: "center", padding: "10px 0" }}>
              <div style={{ fontSize: 64, marginBottom: 12 }}>🎉</div>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: "#1b2559", margin: "0 0 12px" }}>
                Payment Successful!
              </h2>
              <div style={{ background: "#f0fdf4", border: "1.5px solid #16a34a30", borderRadius: 14, padding: 20, marginBottom: 20, textAlign: "left" }}>
                <div style={{ fontWeight: 700, color: "#15803d", fontSize: 14, marginBottom: 6 }}>✅ Account Activated</div>
                <div style={{ fontSize: 13, color: "#166534", lineHeight: 1.8 }}>
                  Your payment was verified instantly via Razorpay.<br/>
                  Your FDS Standard Plan is now <strong>active for 1 year</strong>.<br/>
                  A welcome email has been sent to your registered email.
                </div>
              </div>
              <button
                onClick={() => window.location.reload()}
                style={{ width: "100%", padding: 14, borderRadius: 12, border: "none", cursor: "pointer",
                  background: "#4318FF", color: "#fff", fontWeight: 800, fontSize: 15 }}>
                Go to Dashboard →
              </button>
            </div>
          )}

          {/* ── STEP 2: TRANSACTION ID ── */}
          {step === 2 && (
            <>
              <button onClick={() => setStep(1)}
                style={{ background: "none", border: "none", color: "#4318FF", cursor: "pointer", fontSize: 13, fontWeight: 600, marginBottom: 16, padding: 0 }}>
                ← Back
              </button>

              <h2 style={{ fontSize: 17, fontWeight: 800, color: "#1b2559", margin: "0 0 6px" }}>Enter Transaction ID</h2>
              <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 20 }}>
                Enter your UPI Transaction ID or reference number. Admin will verify and activate your account within 12 hours.
              </p>

              {/* Payment method */}
              <label style={lbl}>Payment Method</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
                {["GPay", "PhonePe", "Paytm", "UPI", "Cash"].map(m => (
                  <button key={m} onClick={() => setMethod(m)}
                    style={{
                      padding: "8px 16px", borderRadius: 8,
                      border: `2px solid ${method === m ? "#4318FF" : "#e0e5f2"}`,
                      background: method === m ? "#ede9fe" : "#fff",
                      color: method === m ? "#4318FF" : "#6b7280",
                      fontWeight: method === m ? 700 : 500,
                      fontSize: 13, cursor: "pointer",
                    }}>
                    {m}
                  </button>
                ))}
              </div>

              {/* Transaction ID input */}
              <div style={{ background: "#f0fdf4", border: "1.5px solid #16a34a40", borderRadius: 12, padding: 16, marginBottom: 20 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#15803d", marginBottom: 8 }}>
                  🔑 Transaction ID / UPI Reference Number <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <input
                  style={{
                    width: "100%", padding: "12px 14px", borderRadius: 8,
                    border: "2px solid #16a34a", background: "#fff",
                    fontSize: 16, fontWeight: 700, letterSpacing: 1,
                    outline: "none", boxSizing: "border-box",
                  }}
                  placeholder="e.g. 607075320137"
                  value={txnId}
                  onChange={e => { setTxnId(e.target.value); setMsg({ type: "", text: "" }); }}
                  onKeyDown={e => e.key === "Enter" && handleSubmit()}
                  autoFocus
                />
                <p style={{ fontSize: 11, color: "#16a34a", margin: "6px 0 0", fontWeight: 600 }}>
                  📱 Find this in your payment app → Transaction History → UTR / Reference number
                </p>
              </div>

              {msg.text && (
                <div style={{
                  padding: "10px 14px", borderRadius: 8, marginBottom: 16,
                  fontWeight: 600, fontSize: 13,
                  background: msg.type === "error" ? "#fee2e2" : "#dcfce7",
                  color:      msg.type === "error" ? "#dc2626"  : "#16a34a",
                }}>
                  {msg.type === "error" ? "❌ " : "✅ "}{msg.text}
                </div>
              )}

              <button onClick={handleSubmit} disabled={submitting || !txnId.trim()}
                style={{
                  width: "100%", padding: 14, borderRadius: 12, border: "none",
                  cursor: submitting || !txnId.trim() ? "not-allowed" : "pointer",
                  background: !txnId.trim() ? "#e0e5f2" : "#4318FF",
                  color: !txnId.trim() ? "#a3aed0" : "#fff",
                  fontWeight: 800, fontSize: 15,
                }}>
                {submitting ? "Submitting..." : "Submit Transaction ID ✓"}
              </button>
            </>
          )}

          {/* ── STEP 3: THANK YOU ── */}
          {step === 3 && (
            <div style={{ textAlign: "center", padding: "10px 0" }}>
              <div style={{ fontSize: 64, marginBottom: 12 }}>🙏</div>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: "#1b2559", margin: "0 0 12px" }}>
                Thank You for Your Payment!
              </h2>

              <div style={{ background: "#f0fdf4", border: "1.5px solid #16a34a30", borderRadius: 14, padding: 20, marginBottom: 20, textAlign: "left" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <span style={{ fontSize: 22 }}>✅</span>
                  <div>
                    <div style={{ fontWeight: 700, color: "#15803d", fontSize: 14, marginBottom: 6 }}>Payment Details Received</div>
                    <div style={{ fontSize: 13, color: "#166534", lineHeight: 1.8 }}>
                      Your payment details are under review.<br/>
                      Transaction ID has been recorded successfully.<br/>
                      <strong>Within 12 hours your account will be activated.</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ background: "#f4f7fe", borderRadius: 10, padding: "12px 16px", marginBottom: 20, textAlign: "left" }}>
                <div style={{ fontSize: 11, color: "#a3aed0", marginBottom: 4, fontWeight: 600 }}>TRANSACTION ID SUBMITTED</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#1b2559", letterSpacing: 1 }}>
                  {txnId || sub?.subscription?.transactionId || "—"}
                </div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>Method: {method}</div>
              </div>

              <div style={{ background: "#fffbeb", borderRadius: 12, padding: 16, marginBottom: 20, textAlign: "left", border: "1.5px solid #fde68a" }}>
                <div style={{ fontWeight: 700, color: "#92400e", fontSize: 13, marginBottom: 8 }}>What happens next?</div>
                <div style={{ fontSize: 12, color: "#92400e", lineHeight: 2 }}>
                  🔍 Admin verifies your Transaction ID<br/>
                  ✅ Account activated within 12 hours<br/>
                  📧 You'll receive a welcome email once activated<br/>
                  🚀 Full access to all FDS features
                </div>
              </div>

              <button onClick={() => { setStep(2); setTxnId(""); }}
                style={{ padding: "10px 20px", background: "#f4f7fe", border: "1.5px solid #e0e5f2", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#4318FF", marginBottom: 12 }}>
                Re-submit Transaction ID
              </button>

              <div>
                <button onClick={() => { localStorage.removeItem("token"); localStorage.removeItem("ownerRole"); window.location.href = "/"; }}
                  style={{ background: "none", border: "none", color: "#a3aed0", cursor: "pointer", fontSize: 12 }}>
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>

        {step !== 3 && step !== 4 && (
          <div style={{ textAlign: "center", marginTop: 14 }}>
            <button onClick={() => { localStorage.removeItem("token"); localStorage.removeItem("ownerRole"); window.location.href = "/"; }}
              style={{ background: "none", border: "none", color: "#a3aed0", cursor: "pointer", fontSize: 12 }}>
              Sign out and use a different account
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const lbl = { display: "block", fontSize: 12, fontWeight: 700, color: "#1b2559", marginBottom: 6 };

export default PaymentActivation;